import { useState } from "react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowUpRight, Mail, Phone, MapPin, MessageCircle } from "lucide-react";

const SERVICE_OPTIONS = [
  "Website / Landing Page",
  "WooCommerce Store",
  "SEO & Local SEO",
  "Lead Generation",
  "WhatsApp / Email Automation",
  "CRM Setup",
  "AI Workflows & n8n / Make",
  "Not sure yet",
];

const BUDGET_OPTIONS = ["₹15K – ₹25K", "₹25K – ₹50K", "₹50K – ₹1L", "₹1L+"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: SERVICE_OPTIONS[0],
    budget: BUDGET_OPTIONS[1],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await submitLead(form);
      setDone(true);
      toast.success("Thanks! We'll reach out within one working day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: SERVICE_OPTIONS[0],
        budget: BUDGET_OPTIONS[1],
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or WhatsApp us.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 border-b hairline"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-y-12 md:gap-x-16">
        <div className="col-span-12 md:col-span-5">
          <div className="section-label mb-4">
            <span className="dot" /> Let's talk
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-balance">
            Tell us where you want to be in{" "}
            <span className="font-serif-accent italic font-normal">
              12 months.
            </span>
          </h2>
          <p className="mt-6 text-[color:var(--ink-2)] max-w-md">
            Share a few details. We'll come back with a clear plan, a fixed
            quote and a free audit of your current funnel — usually within a
            working day.
          </p>

          <div className="mt-10 space-y-4 text-sm">
            <a
              href="mailto:info@cybercart.co.in"
              data-testid="contact-email"
              className="flex items-center gap-3 group"
            >
              <span className="w-9 h-9 grid place-items-center rounded-full border hairline">
                <Mail className="w-4 h-4" />
              </span>
              <span className="group-hover:underline">info@cybercart.co.in</span>
            </a>
            <a
              href="tel:+919320201572"
              data-testid="contact-phone"
              className="flex items-center gap-3 group"
            >
              <span className="w-9 h-9 grid place-items-center rounded-full border hairline">
                <Phone className="w-4 h-4" />
              </span>
              <span className="group-hover:underline">+91 93202 01572</span>
            </a>
            <a
              href="https://wa.me/919320201572"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-center gap-3 group"
            >
              <span className="w-9 h-9 grid place-items-center rounded-full border hairline">
                <MessageCircle className="w-4 h-4" />
              </span>
              <span className="group-hover:underline">
                Chat on WhatsApp — fastest reply
              </span>
            </a>
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 grid place-items-center rounded-full border hairline">
                <MapPin className="w-4 h-4" />
              </span>
              <span>Mumbai · India</span>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="lead-form"
            className="border hairline rounded-lg bg-white p-6 md:p-8 shadow-soft"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Your name" required>
                <Input
                  required
                  data-testid="lead-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Aanya Mehta"
                />
              </Field>
              <Field label="Work email" required>
                <Input
                  required
                  type="email"
                  data-testid="lead-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                />
              </Field>
              <Field label="Phone / WhatsApp" required>
                <Input
                  required
                  data-testid="lead-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 99999 99999"
                />
              </Field>
              <Field label="Company">
                <Input
                  data-testid="lead-company"
                  value={form.company}
                  onChange={update("company")}
                  placeholder="Loomé Studio"
                />
              </Field>
              <Field label="What do you need?">
                <select
                  data-testid="lead-service"
                  value={form.service}
                  onChange={update("service")}
                  className="flex h-10 w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
                >
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </Field>
              <Field label="Budget (approx.)">
                <select
                  data-testid="lead-budget"
                  value={form.budget}
                  onChange={update("budget")}
                  className="flex h-10 w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
                >
                  {BUDGET_OPTIONS.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="mt-4">
              <Field label="What's on your mind?" required>
                <Textarea
                  required
                  data-testid="lead-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Tell us about your business, your stack, and where you'd like help."
                />
              </Field>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <p className="text-xs text-[color:var(--ink-2)]">
                We respect your privacy. No spam, no list selling — ever.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-submit"
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : done ? "Sent ✓" : "Send enquiry"}
                {!submitting && <ArrowUpRight className="w-4 h-4" />}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-[color:var(--ink-2)] mb-2">
        {label}
        {required && <span className="text-[color:var(--accent)]"> *</span>}
      </span>
      {children}
    </label>
  );
}
